import { useEffect, useMemo, useRef, useState } from "react";
import { FiShoppingCart, FiUploadCloud } from "react-icons/fi";
import api from "../services/api";

const tabs = [
  { id: "products", label: "Products" },
  { id: "payment", label: "Payment Status" },
  { id: "purchases", label: "Purchases" },
];

const PAYMENT_TYPES = [
  { id: 1, name: "INDIVIDUAL" },
  { id: 2, name: "GROUP" },
];

const Products = () => {
  const [activeTab, setActiveTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitErrors, setSubmitErrors] = useState([]);
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [purchaseData, setPurchaseData] = useState(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [paymentTypeId, setPaymentTypeId] = useState(1);
  const [submittedPaymentType, setSubmittedPaymentType] = useState("INDIVIDUAL");
  const [groupName, setGroupName] = useState("");
  const [paymentRows, setPaymentRows] = useState([]);
  const [paymentPage, setPaymentPage] = useState(1);
  const [paymentPerPage, setPaymentPerPage] = useState(10);
  const [paymentTotal, setPaymentTotal] = useState(0);
  const [paymentLastPage, setPaymentLastPage] = useState(1);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [paymentRefreshKey, setPaymentRefreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [purchaseRows, setPurchaseRows] = useState([]);
  const [purchasePage, setPurchasePage] = useState(1);
  const [purchasePerPage, setPurchasePerPage] = useState(10);
  const [purchaseTotal, setPurchaseTotal] = useState(0);
  const [purchaseLastPage, setPurchaseLastPage] = useState(1);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseError, setPurchaseError] = useState("");
  const [isBenefitsModalOpen, setIsBenefitsModalOpen] = useState(false);
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const [activePurchase, setActivePurchase] = useState(null);
  const [isRemarksModalOpen, setIsRemarksModalOpen] = useState(false);
  const [activeRemarks, setActiveRemarks] = useState("");

  const statusCards = useMemo(
    () => [
      {
        title: "Product 1",
        description: "Emergency care starter package",
        price: "PHP 1,500.00",
        status: "For Validation",
        badgeClass: "bg-amber-100 text-amber-700",
      },
      {
        title: "Product 2",
        description: "Emergency care family add-on",
        price: "PHP 2,000.00",
        status: "Approved",
        badgeClass: "bg-emerald-100 text-emerald-700",
      },
    ],
    []
  );

  const purchaseCards = useMemo(
    () => [
      {
        title: "Product 1",
        description: "Emergency care starter package",
        price: "PHP 1,500.00",
        status: "Active",
        statusClass: "text-emerald-600",
      },
      {
        title: "Product 2",
        description: "Emergency care family add-on",
        price: "PHP 3,500.00",
        status: "Expired",
        statusClass: "text-slate-400",
      },
    ],
    []
  );

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setSelectedFileName(file.name);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
      setSelectedFileName(file.name);
    }
  };

  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      setFetchError("");
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          setFetchError("Missing access token.");
          return;
        }

        const response = await api.get("/products", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data?.status !== "success") {
          setFetchError("Failed to load products.");
          return;
        }

        const rows = response.data?.data || [];
        setProducts(rows);
        if (rows.length > 0) {
          setSelectedProductId(rows[0].id);
        }
      } catch (error) {
        setFetchError("Failed to load products.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const fetchPayments = async () => {
    setPaymentLoading(true);
    setPaymentError("");
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setPaymentError("Missing access token.");
        return;
      }

      const response = await api.get("/product-purchases", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          page: paymentPage,
          per_page: paymentPerPage,
        },
      });

      if (response.data?.status !== "success") {
        setPaymentError("Failed to load payment status.");
        return;
      }

      const data = response.data?.data || {};
      setPaymentRows(data.rows || []);
      setPaymentTotal(data.total || 0);
      setPaymentLastPage(data.last_page || 1);
    } catch (error) {
      setPaymentError("Failed to load payment status.");
    } finally {
      setPaymentLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [paymentPage, paymentPerPage, paymentRefreshKey]);

  useEffect(() => {
    const fetchApprovedPurchases = async () => {
      setPurchaseLoading(true);
      setPurchaseError("");
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          setPurchaseError("Missing access token.");
          return;
        }

        const response = await api.get("/product-purchases/approved", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            page: purchasePage,
            per_page: purchasePerPage,
          },
        });

        if (response.data?.status !== "success") {
          setPurchaseError("Failed to load purchases.");
          return;
        }

        const data = response.data?.data || {};
        setPurchaseRows(data.rows || []);
        setPurchaseTotal(data.total || 0);
        setPurchaseLastPage(data.last_page || 1);
      } catch (error) {
        setPurchaseError("Failed to load purchases.");
      } finally {
        setPurchaseLoading(false);
      }
    };

    fetchApprovedPurchases();
  }, [purchasePage, purchasePerPage]);

  const formatAmount = (value) => {
    const amount = Number(value);
    if (Number.isNaN(amount)) {
      return "PHP 0.00";
    }
    return `PHP ${amount.toFixed(2)}`;
  };

  const selectedProduct = products.find((product) => product.id === selectedProductId);
  const today = new Date().toISOString().split("T")[0];

  const handleOpenPurchase = (productId) => {
    if (!productId) {
      setSubmitErrors(["Please select your products first."]);
      setIsErrorModalOpen(true);
      return;
    }
    setSelectedProductId(productId);
    setSubmitErrors([]);
    setIsErrorModalOpen(false);
    setPaymentTypeId(1);
    setGroupName("");
    setPaymentDate("");
    setSelectedFile(null);
    setSelectedFileName("");
    setIsPurchaseModalOpen(true);
  };

  const handleSubmitPurchase = async () => {
    setIsSubmitting(true);
    setSubmitErrors([]);
    setSubmitSuccess("");
    setIsErrorModalOpen(false);

    try {
      if (!selectedProductId) {
        setSubmitErrors(["Please select your products first."]);
        setIsErrorModalOpen(true);
        return;
      }

      if (!paymentDate) {
        setSubmitErrors(["Date of payment is required."]);
        setIsErrorModalOpen(true);
        return;
      }
      if (paymentDate > today) {
        setSubmitErrors(["Date of payment cannot be in the future."]);
        setIsErrorModalOpen(true);
        return;
      }

      const selectedPaymentType = PAYMENT_TYPES.find((type) => type.id === paymentTypeId);
      if (!selectedPaymentType) {
        setSubmitErrors(["Invalid payment type."]);
        setIsErrorModalOpen(true);
        return;
      }

      if (selectedPaymentType.name === "INDIVIDUAL" && !selectedFile) {
        setSubmitErrors(["Please attach your proof of payment and click Submit."]);
        setIsErrorModalOpen(true);
        return;
      }

      if (selectedPaymentType.name === "GROUP" && !groupName.trim()) {
        setSubmitErrors(["Group name is required for group payments."]);
        setIsErrorModalOpen(true);
        return;
      }

      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setSubmitErrors(["Missing access token."]);
        setIsErrorModalOpen(true);
        return;
      }

      const formData = new FormData();
      formData.append("product_id", selectedProductId);
      formData.append("payment_date", paymentDate);
      formData.append("payment_type_id", paymentTypeId);
      if (selectedPaymentType.name === "GROUP") {
        formData.append("group_name", groupName.trim());
      }
      if (selectedPaymentType.name === "INDIVIDUAL" && selectedFile) {
        formData.append("file", selectedFile);
      }

      const response = await api.post("/product-purchases", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data?.status !== "success") {
        const message = response.data?.message || "Failed to submit purchase.";
        if (message && typeof message === "object") {
          setSubmitErrors(Object.values(message).flat());
        } else {
          setSubmitErrors([message]);
        }
        setIsErrorModalOpen(true);
        return;
      }

      setPurchaseData(response.data?.data || null);
      setSubmitSuccess(response.data?.message || "Purchase submitted.");
      setSubmittedPaymentType(selectedPaymentType.name);
      setIsSuccessModalOpen(true);
      setIsPurchaseModalOpen(false);
      setSelectedFile(null);
      setSelectedFileName("");
      setPaymentDate("");
      setPaymentPage(1);
      setPaymentRefreshKey((prev) => prev + 1);
    } catch (error) {
      const message = error?.response?.data?.message;
      if (message && typeof message === "object") {
        setSubmitErrors(Object.values(message).flat());
      } else if (typeof message === "string") {
        setSubmitErrors([message]);
      } else {
        setSubmitErrors(["Failed to submit purchase."]);
      }
      setIsErrorModalOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefreshProof = async (uploadId) => {
    if (!uploadId) {
      return;
    }

    setIsRefreshing(true);
    setPaymentError("");
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setPaymentError("Missing access token.");
        return;
      }

      const response = await api.post(
        "/product-purchases/refresh",
        { upload_id: uploadId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.status !== "success") {
        setPaymentError("Failed to refresh proof link.");
        return;
      }

      const refreshed = response.data?.data;
      setPaymentRows((prev) =>
        prev.map((row) =>
          row.upload_id === uploadId
            ? {
                ...row,
                signed_url: refreshed?.signed_url || row.signed_url,
                url_expires_at: refreshed?.url_expires_at || row.url_expires_at,
              }
            : row
        )
      );
    } catch (error) {
      setPaymentError("Failed to refresh proof link.");
    } finally {
      setIsRefreshing(false);
    }
  };

  const isUrlExpired = (expiresAt) => {
    if (!expiresAt) {
      return true;
    }
    const expiry = new Date(expiresAt);
    if (Number.isNaN(expiry.getTime())) {
      return true;
    }
    return expiry.getTime() <= Date.now();
  };

  const getStatusBadgeClass = (statusName) => {
    const normalized = String(statusName || "").toLowerCase();
    if (normalized === "for validation") {
      return "bg-amber-100 text-amber-700";
    }
    if (normalized === "approved" || normalized === "paid") {
      return "bg-emerald-100 text-emerald-700";
    }
    if (normalized === "rejected") {
      return "bg-rose-100 text-rose-700";
    }
    if (normalized === "cancelled" || normalized === "canceled") {
      return "bg-red-100 text-red-700";
    }
    return "bg-slate-100 text-slate-600";
  };

  const formatDateTime = (value) => {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return date.toLocaleString("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const isExpired = (value) => {
    if (!value) {
      return false;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return false;
    }
    return date.getTime() < Date.now();
  };

  const renderPaymentPages = () => {
    const buttons = [];
    const start = Math.max(1, paymentPage - 2);
    const end = Math.min(paymentLastPage, start + 4);
    for (let p = start; p <= end; p += 1) {
      buttons.push(
        <button
          key={p}
          type="button"
          onClick={() => setPaymentPage(p)}
          className={`min-w-[40px] rounded-xl border px-3 py-2 text-sm font-semibold ${
            p === paymentPage
              ? "border-[var(--brand-orange)] bg-orange-50 text-[var(--brand-orange-dark)]"
              : "border-slate-200 text-slate-600 hover:border-slate-300"
          }`}
        >
          {p}
        </button>
      );
    }
    return buttons;
  };

  const renderPurchasePages = () => {
    const buttons = [];
    const start = Math.max(1, purchasePage - 2);
    const end = Math.min(purchaseLastPage, start + 4);
    for (let p = start; p <= end; p += 1) {
      buttons.push(
        <button
          key={p}
          type="button"
          onClick={() => setPurchasePage(p)}
          className={`min-w-[40px] rounded-xl border px-3 py-2 text-sm font-semibold ${
            p === purchasePage
              ? "border-[var(--brand-orange)] bg-orange-50 text-[var(--brand-orange-dark)]"
              : "border-slate-200 text-slate-600 hover:border-slate-300"
          }`}
        >
          {p}
        </button>
      );
    }
    return buttons;
  };

  const handleSuccessClose = () => {
    setIsSuccessModalOpen(false);
  };

  const handleSuccessConfirm = () => {
    setIsSuccessModalOpen(false);
    setActiveTab("payment");
    setPaymentRefreshKey((prev) => prev + 1);
  };

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6">
      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
          Products
        </p>
        <h1 className="mt-3 text-3xl font-semibold text-slate-900">
          Manage your emergency care packages
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Review packages, upload payment proof, and track validation status in
          one streamlined view.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-white/60 bg-white/80 p-2 shadow-sm shadow-slate-200/60">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
              activeTab === tab.id
                ? "bg-[var(--brand-blue)] text-white shadow-md shadow-blue-200"
                : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "products" && (
        <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
            {fetchError && (
              <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                {fetchError}
              </div>
            )}

            {isLoading ? (
              <p className="text-sm text-slate-400">Loading products...</p>
            ) : products.length === 0 ? (
              <p className="text-sm text-slate-400">No active products found.</p>
            ) : (
              <div className="space-y-6">
                {products.map((product) => {
                  const isSelected = product.id === selectedProductId;
                  return (
                    <div
                      key={product.id}
                      className={`rounded-2xl border px-5 py-4 transition ${
                        isSelected
                          ? "border-[var(--brand-blue)] bg-blue-50/40"
                          : "border-slate-200 bg-white"
                      }`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="max-w-xl">
                          <h2 className="text-2xl font-semibold text-slate-900">
                            {product.title}
                          </h2>
                          {product.secondary_title && (
                            <p className="mt-2 text-sm text-slate-500">
                              {product.secondary_title}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-semibold text-[var(--brand-blue)]">
                            {formatAmount(product.amount)}
                          </p>
                          <span className="mt-2 inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-[var(--brand-blue)]">
                            {product.validity_days} Days Validity
                          </span>
                        </div>
                      </div>

                      <div className="mt-4">
                        {product.product_image ? (
                          <img
                            src={product.product_image}
                            alt={product.title}
                            className="h-64 w-full rounded-2xl border border-slate-200 object-contain bg-white"
                          />
                        ) : (
                          <div className="flex h-48 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-400">
                            No product image yet.
                          </div>
                        )}
                      </div>

                      <div className="mt-5 flex flex-wrap items-center gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setActivePurchase(product);
                            setIsBenefitsModalOpen(true);
                          }}
                          className="rounded-2xl bg-[var(--brand-orange)] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
                        >
                          Medical Benefits
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOpenPurchase(product.id)}
                          className={`rounded-2xl px-4 py-2 text-sm font-semibold transition ${
                            isSelected
                              ? "bg-[var(--brand-blue)] text-white shadow-lg shadow-blue-200"
                              : "border border-slate-200 text-slate-600 hover:border-slate-300"
                          }`}
                        >
                          <span className="inline-flex items-center gap-2">
                            <FiShoppingCart />
                            Purchase
                          </span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
            <h3 className="text-xl font-semibold text-[var(--brand-blue)]">
              Bank Transfer or Deposit
            </h3>
            <div className="mt-4 space-y-3 text-sm text-slate-600">
              <div className="text-base font-semibold text-slate-900">
                Bank of the Philippine Islands (BPI)
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Account Name</span>
                <span className="font-semibold text-slate-800">
                  RMC2 Consultancy Corp
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Account Number</span>
                <span className="font-semibold text-slate-800">
                  006371-0103-17
                </span>
              </div>
            </div>
            <div className="mt-4 text-sm text-slate-500">
              Click Purchase on your chosen product to enter payment details and submit proof.
            </div>
          </div>
        </div>
      )}

      {activeTab === "payment" && (
        <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">
                Payment Status
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                Track validation progress for your submitted purchases.
              </p>
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span>Display</span>
              <select
                value={paymentPerPage}
                onChange={(event) => {
                  setPaymentPerPage(Number(event.target.value));
                  setPaymentPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {[5, 10, 20, 50, 100].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {paymentError && (
            <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
              {paymentError}
            </div>
          )}

          <div className="mt-6 space-y-4">
            {paymentLoading ? (
              <p className="text-sm text-slate-400">Loading payments...</p>
            ) : paymentRows.length === 0 ? (
              <p className="text-sm text-slate-400">No payment records found.</p>
            ) : (
              paymentRows.map((row) => {
                const expired = isUrlExpired(row.url_expires_at);
                return (
                  <div
                    key={row.id}
                    className="rounded-2xl border border-slate-200 bg-white p-5"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-semibold text-[var(--brand-blue)]">
                          {row.title}
                        </h3>
                        {row.secondary_title && (
                          <p className="mt-1 text-sm text-slate-500">
                            {row.secondary_title}
                          </p>
                        )}
                      </div>
                      <p className="text-lg font-semibold text-[var(--brand-blue)]">
                        {formatAmount(row.amount)}
                      </p>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() => row.signed_url && window.open(row.signed_url, "_blank")}
                        disabled={!row.signed_url}
                        className="text-sm font-semibold text-[var(--brand-blue)] transition hover:text-[var(--brand-blue-2)] disabled:text-slate-400"
                      >
                        View Proof of Payment
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRefreshProof(row.upload_id)}
                        disabled={!row.upload_id || !expired || isRefreshing}
                        className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isRefreshing ? "Refreshing..." : "Refresh Link"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveRemarks(row.remarks || "");
                          setIsRemarksModalOpen(true);
                        }}
                        className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                      >
                        View Remarks
                      </button>
                      <span className="text-xs text-slate-400">
                        {expired ? "Expired" : "Active"}
                      </span>
                      <span
                        className={`rounded-full px-4 py-2 text-xs font-semibold ${getStatusBadgeClass(
                          row.status_name
                        )}`}
                      >
                        {row.status_name || "For Validation"}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
            <div>
              Showing {paymentRows.length} of {paymentTotal} entries
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setPaymentPage(1)}
                disabled={paymentPage === 1}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                First
              </button>
              <button
                type="button"
                onClick={() => setPaymentPage((prev) => Math.max(1, prev - 1))}
                disabled={paymentPage === 1}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Previous
              </button>
              {renderPaymentPages()}
              <button
                type="button"
                onClick={() => setPaymentPage((prev) => Math.min(paymentLastPage, prev + 1))}
                disabled={paymentPage === paymentLastPage}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Next
              </button>
              <button
                type="button"
                onClick={() => setPaymentPage(paymentLastPage)}
                disabled={paymentPage === paymentLastPage}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Last
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "purchases" && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
            <p>Approved purchases</p>
            <div className="flex items-center gap-3">
              <span>Display</span>
              <select
                value={purchasePerPage}
                onChange={(event) => {
                  setPurchasePerPage(Number(event.target.value));
                  setPurchasePage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {[5, 10, 20, 50, 100].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {purchaseError && (
            <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
              {purchaseError}
            </div>
          )}

          {purchaseLoading ? (
            <p className="text-sm text-slate-400">Loading purchases...</p>
          ) : purchaseRows.length === 0 ? (
            <p className="text-sm text-slate-400">No approved purchases found.</p>
          ) : (
            purchaseRows.map((row) => (
              <div
                key={`${row.id}-${row.status_id}`}
                className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-semibold text-[var(--brand-blue)]">
                      {row.title}
                    </h3>
                    {row.secondary_title && (
                      <p className="mt-1 text-sm text-slate-500">
                        {row.secondary_title}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-6">
                    <p className="text-xl font-semibold text-[var(--brand-blue)]">
                      {formatAmount(row.amount)}
                    </p>
                    <span className="text-sm font-semibold text-emerald-600">
                      {row.status_name || "Approved"}
                    </span>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        isExpired(row.expiration_date)
                          ? "bg-rose-100 text-rose-700"
                          : "bg-emerald-100 text-emerald-700"
                      }`}
                    >
                      {isExpired(row.expiration_date) ? "Expired" : "Active"}
                    </span>
                  </div>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {[
                    { label: "Date of Payment", value: formatDateTime(row.payment_date) },
                    { label: "Activation Date of Medical Benefits", value: formatDateTime(row.activation_date) },
                    { label: "Expiration Date", value: formatDateTime(row.expiration_date) },
                    ...(row.consumed_at
                      ? [{ label: "Consumed Date", value: formatDateTime(row.consumed_at) }]
                      : []),
                  ].map((detail) => (
                    <div
                      key={detail.label}
                      className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-center"
                    >
                      <p className="text-xs uppercase tracking-wide text-slate-400">
                        {detail.label}
                      </p>
                      <p className="mt-2 text-sm font-semibold text-slate-700">
                        {detail.value || "-"}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setActivePurchase(row);
                      setIsBenefitsModalOpen(true);
                    }}
                    className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
                  >
                    Medical Benefits
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActivePurchase(row);
                      setIsCardModalOpen(true);
                    }}
                    className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
                  >
                    View Virtual Card
                  </button>
                </div>
              </div>
            ))
          )}

          <div className="mt-2 flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
            <div>
              Showing {purchaseRows.length} of {purchaseTotal} entries
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setPurchasePage(1)}
                disabled={purchasePage === 1}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                First
              </button>
              <button
                type="button"
                onClick={() => setPurchasePage((prev) => Math.max(1, prev - 1))}
                disabled={purchasePage === 1}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Previous
              </button>
              {renderPurchasePages()}
              <button
                type="button"
                onClick={() => setPurchasePage((prev) => Math.min(purchaseLastPage, prev + 1))}
                disabled={purchasePage === purchaseLastPage}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Next
              </button>
              <button
                type="button"
                onClick={() => setPurchasePage(purchaseLastPage)}
                disabled={purchasePage === purchaseLastPage}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Last
              </button>
            </div>
          </div>
        </div>
      )}

      {isPurchaseModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-500">
                  Purchase
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Bank Instructions & Submission
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Thank you for purchasing {selectedProduct?.title || "this product"}!
                  <br/><br/>
                  To complete your transaction, please proceed with payment via bank transfer or deposit:
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsPurchaseModalOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-1 space-y-2 rounded-2xl border border-amber-100 bg-amber-50/60 p-4 text-sm text-slate-700">
              <div className="flex items-center justify-between gap-4">
                <span className="text-slate-500">Bank</span>
                <span className="font-semibold text-slate-900">BPI</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-slate-500">Account Name</span>
                <span className="font-semibold text-slate-900">
                  RMC2 Consultancy Corp
                </span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-slate-500">Account Number</span>
                <span className="font-semibold text-slate-900">
                  006371-0103-17
                </span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-slate-500">Amount to Pay</span>
                <span className="font-semibold text-slate-900">
                  {selectedProduct ? formatAmount(selectedProduct.amount) : "-"}
                </span>
              </div>
            </div>

            <p className="mt-2 text-sm text-slate-500">
              After payment, kindly upload a copy of your proof of payment for validation and product activation.
            </p>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Payment Type
                </label>
                <div className="flex flex-wrap gap-3">
                  {PAYMENT_TYPES.map((type) => (
                    <label
                      key={type.id}
                      className={`inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm font-semibold transition ${
                        paymentTypeId === type.id
                          ? "border-[var(--brand-orange)] bg-orange-50 text-[var(--brand-orange-dark)]"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment_type_id"
                        value={type.id}
                        checked={paymentTypeId === type.id}
                        onChange={() => setPaymentTypeId(type.id)}
                        className="text-[var(--brand-orange)] focus:ring-[var(--brand-orange)]"
                      />
                      {type.name}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Date of Payment
                </label>
                <input
                  type="date"
                  value={paymentDate}
                  max={today}
                  onChange={(event) => setPaymentDate(event.target.value)}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
            </div>

            {paymentTypeId === 2 && (
              <div className="mt-4 flex flex-col gap-2">
                <label className="text-xs font-semibold text-slate-500">
                  Group Name
                </label>
                <input
                  type="text"
                  value={groupName}
                  onChange={(event) => setGroupName(event.target.value)}
                  placeholder="Enter group name"
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
            )}

            {paymentTypeId === 1 && (
              <div
                className={`mt-4 rounded-2xl border-2 border-dashed px-6 py-8 text-center text-sm transition ${
                  isDragActive
                    ? "border-orange-300 bg-orange-50"
                    : "border-slate-200 bg-slate-50"
                }`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDragActive(true);
                }}
                onDragLeave={() => setIsDragActive(false)}
                onDrop={handleDrop}
              >
                <FiUploadCloud className="mx-auto mb-3 text-2xl text-[var(--brand-orange)]" />
                <p className="font-semibold text-[var(--brand-orange)]">
                  {selectedFileName
                    ? `File selected: ${selectedFileName}`
                    : "Upload Proof of Payment"}
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  Drag and drop or click to choose a file.
                </p>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-4 rounded-2xl bg-slate-500 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-slate-600"
                >
                  Choose File
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                />
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsPurchaseModalOpen(false)}
                className="rounded-2xl border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitPurchase}
                disabled={isSubmitting}
                className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isSuccessModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-500">
                  Success
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Purchase Submitted
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Thank you for purchasing {selectedProduct?.title || "this product"}!
                </p>
              </div>
              <button
                type="button"
                onClick={handleSuccessClose}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 space-y-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4 text-sm text-slate-700">
              {submittedPaymentType === "GROUP" ? (
                <div className="space-y-2">
                  <p className="font-semibold text-slate-900">Please ensure that the proof of payment and the list of members are sent to info@rmc2.com.ph for validation. Once received and verified, validation may take 3–5 working days. Rest assured, this will not affect your product activation.</p>
                  <p>Thank you.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="font-semibold text-slate-900">Thank you for your payment! Please allow 3–5 working days for validation. Rest assured, this will not affect your product activation.</p>
                  <p>Thank you.</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={handleSuccessConfirm}
                className="rounded-2xl bg-emerald-500 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-200 hover:bg-emerald-600"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {isErrorModalOpen && submitErrors.length > 0 && !isSuccessModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-rose-500">
                  Error
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Purchase Failed
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Please review the issues below and try again.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsErrorModalOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 space-y-3 rounded-2xl border border-rose-100 bg-rose-50/60 p-4 text-sm text-rose-700">
              <ul className="space-y-2">
                {submitErrors.map((item, index) => (
                  <li key={`${item}-${index}`} className="flex gap-2">
                    <span className="mt-1 h-2 w-2 rounded-full bg-rose-400" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setIsErrorModalOpen(false)}
                className="rounded-2xl bg-rose-500 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-rose-200 hover:bg-rose-600"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {isBenefitsModalOpen && activePurchase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Product Details
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  {activePurchase.title}
                </h3>
                {activePurchase.secondary_title && (
                  <p className="mt-2 text-sm text-slate-500">
                    {activePurchase.secondary_title}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setIsBenefitsModalOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 space-y-3 text-sm text-slate-600">
              {activePurchase.description ? (
                <p className="whitespace-pre-line">{activePurchase.description}</p>
              ) : (
                <p>No description available.</p>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setIsBenefitsModalOpen(false)}
                className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {isCardModalOpen && activePurchase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Virtual Card
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  {activePurchase.title}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsCardModalOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6">
              {activePurchase.product_image ? (
                <img
                  src={activePurchase.product_image}
                  alt={activePurchase.title}
                  className="w-full rounded-2xl border border-slate-200"
                />
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
                  No card image available.
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setIsCardModalOpen(false)}
                className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {isRemarksModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Payment Remarks
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Remarks Details
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsRemarksModalOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              {activeRemarks ? (
                <p className="whitespace-pre-line">{activeRemarks}</p>
              ) : (
                <p>No remarks available.</p>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setIsRemarksModalOpen(false)}
                className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
